
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface DeliveryAddress {
  id: string;
  organization_id: string;
  contact_name: string;
  company_name?: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  postal_code: string;
  country: string;
  phone_number: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export const useDeliveryAddresses = (organizationId?: string) => {
  return useQuery({
    queryKey: ['delivery-addresses', organizationId],
    queryFn: async () => {
      let query = supabase
        .from('delivery_addresses')
        .select('*')
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false });

      if (organizationId) {
        query = query.eq('organization_id', organizationId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as DeliveryAddress[];
    },
    enabled: !!organizationId,
  });
};

export const useCreateDeliveryAddress = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (address: Omit<DeliveryAddress, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('delivery_addresses')
        .insert(address)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-addresses'] });
      toast({
        title: "Success",
        description: "Delivery address created successfully",
      });
    },
    onError: (error) => {
      console.error("Error creating delivery address:", error);
      toast({
        title: "Error",
        description: "Failed to create delivery address",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateDeliveryAddress = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<DeliveryAddress> & { id: string }) => {
      const { data, error } = await supabase
        .from('delivery_addresses')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-addresses'] });
      toast({
        title: "Success",
        description: "Delivery address updated successfully",
      });
    },
    onError: (error) => {
      console.error("Error updating delivery address:", error);
      toast({
        title: "Error",
        description: "Failed to update delivery address",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteDeliveryAddress = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('delivery_addresses')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-addresses'] });
      toast({
        title: "Success",
        description: "Delivery address deleted successfully",
      });
    },
    onError: (error) => {
      console.error("Error deleting delivery address:", error);
      toast({
        title: "Error",
        description: "Failed to delete delivery address",
        variant: "destructive",
      });
    },
  });
};
